import { ReactNode } from 'react'
import ReactDOMServer from 'react-dom/server'
import { ColumnInstance } from 'react-table'

import { isStringOrNumber } from '../../../utils/isStringOrNumber'
import { InternalAccessor } from '../enums/accessors'
import { TableToggleableColumn } from '../types/column'

const HTML_ENTITY_MAP: Record<string, string> = {
  '&amp;': '&',
  '&gt;': '>',
  '&lt;': '<',
  '&nbsp;': ' ',
  '&quot;': '"',
  '&#39;': "'",
  '&#x27;': "'",
}
const HTML_ENTITY_REGEX = new RegExp(Object.keys(HTML_ENTITY_MAP).join('|'), 'g')

/* Scanned instead of matched with a regex: a `<[^>]*>` pattern backtracks and runs in super-linear time */
const stripHtmlTags = (markup: string): string => {
  let text = ''
  let cursor = 0

  while (cursor < markup.length) {
    const tagStart = markup.indexOf('<', cursor)

    if (tagStart === -1) {
      return text + markup.slice(cursor)
    }

    text += markup.slice(cursor, tagStart)

    const tagEnd = markup.indexOf('>', tagStart)

    if (tagEnd === -1) {
      return text
    }

    cursor = tagEnd + 1
  }

  return text
}

const extractTextFromReactNode = (node: ReactNode): string => {
  if (!node) {
    return ''
  }

  if (isStringOrNumber(node)) {
    return String(node)
  }

  try {
    return stripHtmlTags(ReactDOMServer.renderToStaticMarkup(node))
      .replace(HTML_ENTITY_REGEX, (entity) => HTML_ENTITY_MAP[entity] ?? entity)
      .trim()
  } catch {
    return ''
  }
}

type ColumnWithOptionalHeaderName = ColumnInstance<any> | (ColumnInstance<any> & { headerName?: string })

export const getToggleableColumns = (columns: ColumnWithOptionalHeaderName[]): TableToggleableColumn[] => {
  const accessibleColumns = columns.filter(
    (column) => !(Object.values(InternalAccessor) as string[]).includes(column.id),
  )

  return accessibleColumns
    .map((column) => {
      const header = column.Header ? column.render('Header') : null
      /* A very special case: if the header is wrapped with the tooltip, we won't be able to extract the text properly */
      /* We use a headerName prop on column for that, which is technically extention of the ColumnInstance react-table's interface */
      const headerName = 'headerName' in column ? (column.headerName ?? '') : ''
      const columnName = headerName || (header ? extractTextFromReactNode(header) : '')

      const columnProps = column.getToggleHiddenProps()

      return {
        checked: columnProps.checked,
        id: column.id,
        name: columnName || '',
      }
    })
    .filter(({ name }) => !!name)
}
